import { ApiService } from "@core/api/api-service";
import { addAuthorizationInterceptor } from "@core/api/interceptors/add-authorization-interceptor";
import { addBaseUrlInterceptor } from "@core/api/interceptors/add-base-url-interceptor";
import { errorHandlingInterceptor } from "@core/api/interceptors/error-handling-interceptor";
import { getLatestAnnouncementId } from "@core/api/interceptors/get-latest-announcement-id-interceptor";
import { getPTStatusInterceptor } from "@core/api/interceptors/get-pt-status-interceptor";
import { getHcpStatusInterceptor } from "@core/api/interceptors/get-user-hcp-status-interceptor";
import { getIdentityStatusInterceptor } from "@core/api/interceptors/get-identity-status-interceptor";
import { addRequestInterceptor, addResponseInterceptor } from "@core/api/interceptors/interceptor";
import { notificationCountInterceptor } from "@core/api/interceptors/notification-count-interceptor";
import { ErrorManager } from "@core/error-manager";
import { AnnouncementReceivedService } from "@domain/announcement/announcement-received-service";
import { AuthService } from "@domain/authentication/auth-service";
import { InfraManager } from "@domain/infra/infra-manager";
import { UserStatusManager } from "@domain/user/user-status-manager";
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export class AuthenticatedApiService implements ApiService {
	axiosInstance: AxiosInstance;

	constructor(
		private readonly axiosInstanceProvider: () => AxiosInstance,
		private readonly authService: AuthService,
		private readonly infraManager: InfraManager,
		private readonly userStatusManager: UserStatusManager,
		private readonly announcementReceivedService: AnnouncementReceivedService,
		private readonly errorManager: ErrorManager,
	) {
		this.axiosInstance = this.initiateAxiosInstance();
		infraManager.targetInfraChanged.add(this.refreshAxiosInstance.bind(this));
	}

	private refreshAxiosInstance(): void {
		this.axiosInstance = this.initiateAxiosInstance();
	}

	private initiateAxiosInstance(): AxiosInstance {
		const axiosInstance = this.axiosInstanceProvider();
		addRequestInterceptor(axiosInstance, addBaseUrlInterceptor(this.infraManager));
		addRequestInterceptor(axiosInstance, addAuthorizationInterceptor(this.authService));

		addResponseInterceptor(axiosInstance, errorHandlingInterceptor(this.errorManager));
		addResponseInterceptor(axiosInstance, getPTStatusInterceptor(this.userStatusManager));
		addResponseInterceptor(axiosInstance, getHcpStatusInterceptor(this.userStatusManager));
		addResponseInterceptor(axiosInstance, getIdentityStatusInterceptor(this.userStatusManager));
		addResponseInterceptor(axiosInstance, notificationCountInterceptor());
		addResponseInterceptor(axiosInstance, getLatestAnnouncementId(this.announcementReceivedService));

		return axiosInstance;
	}

	get<T = unknown, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		return this.axiosInstance.get(url, config);
	}

	post<T = unknown, R = AxiosResponse<T>>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<R> {
		return this.axiosInstance.post(url, data, config);
	}

	put<T = unknown, R = AxiosResponse<T>>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<R> {
		return this.axiosInstance.put(url, data, config);
	}

	delete<T = unknown, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		return this.axiosInstance.delete(url, config);
	}
}
